import _ from 'lodash'
import { GetterTree } from 'vuex'
import { PERMISSIONS } from '@/store/share/constants'
import { EditorState, Stencil, EntryErrorSet } from '@/store/editor/types'
import { RootState } from '@/store/types'
import { Maybe } from '@/types/general'
import { UnencryptedContent } from '@/types/crypto'
import { Permission } from '@/store/share/types'
import Entry from '@/data/entry-model'

const getters: GetterTree<EditorState, RootState> = {
    /**
     * Get the currently selected vault object
     */
    selectedVault({ entry }): Maybe<Entry> {
        return entry.model && entry.model.parent
    },

    /**
     * Get the current stencil
     */
    entryStencil({ stencils, entry }): Stencil {
        return stencils[entry.schema]
    },

    /**
     * Get the content of the current entry ready to encrypt
     */
    content({ entry }): UnencryptedContent {
        const data: UnencryptedContent = {}
        Object.keys(entry.fields).forEach((key: string) => {
            data[key] = entry.fields[key].value!
        })
        return data
    },

    /**
     * Get a list of errors for the current entry
     */
    entryErrors({ entry }, { entryStencil }: { entryStencil: Stencil }): EntryErrorSet {
        const errors: EntryErrorSet = { fields: {} }
        if (entry.schema !== 'vault' && entry.vault === undefined) {
            errors.vault = 'Parent vault required'
        }
        Object.keys(entry.fields).forEach((key) => {
            const stencil = entryStencil.fields[key]
            if (stencil.required && entry.fields[key].value === '') {
                errors.fields[key] = `${stencil.name} is required`
            }
        })
        return errors
    },

    /**
     * Check if there are any errors with the current entry
     */
    hasErrors(_state, { entryErrors }: { entryErrors: EntryErrorSet }): boolean {
        return entryErrors.vault !== undefined ||
                entryErrors.tags !== undefined ||
                Object.keys(entryErrors.fields).length > 0
    },

    /**
     * Get the user's permission definition for the current entry
     */
    userPermission({ entry }): Permission {
        return PERMISSIONS[entry.permission] || PERMISSIONS.creator
    },

    /**
     * Get the user's permission level for the current entry
     */
    userPermissionLevel(_state, { userPermission }: { userPermission: Permission }): number {
        return userPermission.position
    },

    /**
     * Get all permissions that the user can assign to other users
     */
    assignablePermissions(
        _state,
        { userPermissionLevel }: { userPermissionLevel: number }
    ): Permission[] {
        return _.filter(PERMISSIONS, v => v.position <= userPermissionLevel && v.position !== 3)
    },

    /**
     * Check if the current user is the creator of the entry
     */
    isCreator(_state, { userPermissionLevel }: { userPermissionLevel: number }): boolean {
        return userPermissionLevel >= PERMISSIONS.creator.position
    },

    /**
     * Check if the current user has manage permission on the entry (or above)
     */
    canManage(_state, { userPermissionLevel }: { userPermissionLevel: number }): boolean {
        return userPermissionLevel >= PERMISSIONS.manage.position
    },

    /**
     * Check if the current user has modify permission on the entry (or above)
     */
    canModify(_state, { userPermissionLevel }: { userPermissionLevel: number }): boolean {
        return userPermissionLevel >= PERMISSIONS
            .modify
            .position
    }
}

export default getters
